/**
 * Authentication Provider
 *
 * Provides auth state and methods to the entire application.
 * Blueprint Task: 2.2.2
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

import {
  login as authLogin,
  logout as authLogout,
  register as authRegister,
  confirmRegistration,
  forgotPassword,
  confirmForgotPassword,
  completeNewPassword as authCompleteNewPassword,
  getCurrentAuthUser,
  isAuthenticated,
  signInWithOAuth as authSignInWithOAuth,
  onOAuthSignIn,
  type AuthUser,
  type OAuthProvider,
} from '../lib/auth';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<{ needsConfirmation: boolean }>;
  confirmSignup: (email: string, code: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  loginWithOAuth: (provider: OAuthProvider) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 *
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentAuthUser();
      setUser(currentUser);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const PUBLIC_PATHS = ['/guest-checkout', '/checkout/confirmation', '/activate', '/login', '/forgot-password', '/signup'];
    const isPublicPath = PUBLIC_PATHS.some(p => window.location.pathname.startsWith(p));

    async function checkAuth() {
      if (isPublicPath) {
        setIsLoading(false);
        return;
      }
      try {
        const authenticated = await isAuthenticated();
        if (authenticated) {
          await refreshUser();
        }
      } catch {
        // Clear stale Amplify tokens so subsequent navigations don't repeat failed refresh
        try { await authLogout(); } catch { /* already signed out */ }
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    void checkAuth();

    const unsubscribe = onOAuthSignIn(async (event) => {
      if (event === 'success') {
        try { await refreshUser(); } catch { setUser(null); }
      }
    });
    return unsubscribe;
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    const authUser = await authLogin(email, password);
    setUser(authUser);
  };

  const logout = async () => {
    await authLogout();
    setUser(null);
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    const result = await authRegister(email, password, firstName, lastName);
    return { needsConfirmation: !result.isSignUpComplete };
  };

  const confirmSignup = async (email: string, code: string) => {
    await confirmRegistration(email, code);
  };

  const resetPassword = async (email: string) => {
    await forgotPassword(email);
  };

  const confirmResetPasswordFn = async (email: string, code: string, newPassword: string) => {
    await confirmForgotPassword(email, code, newPassword);
  };

  const completeNewPassword = async (newPassword: string) => {
    const authUser = await authCompleteNewPassword(newPassword);
    setUser(authUser);
  };

  const loginWithOAuth = (provider: OAuthProvider) => {
    return authSignInWithOAuth(provider);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    register,
    confirmSignup,
    resetPassword,
    confirmResetPassword: confirmResetPasswordFn,
    completeNewPassword,
    loginWithOAuth,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 *
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
